import React, { useState, useEffect, useCallback } from "react";
import {
  Header,
  Icon,
  Message,
  Table,
  Segment,
  Modal,
  Dimmer,
  Loader,
  Button,
  Form,
  Input,
} from "semantic-ui-react";
import { Formik } from "formik";
import * as Yup from "yup";
import { normalizeId } from "../../../../../../formatting/inventoryIds";
import axios from "../../../../../../../apis/dvdlocator";
import { partNumberStringSchema } from "../../../../../../../validation/partNumberYupSchema";

const fetchProductsForInventoryId = async (inventoryId) => {
  return axios.get(`/products?inventoryId=${inventoryId}`);
};

const updateComponentPartNumber = async (inventoryId, newPartNumber) => {
  return axios.put(`/inventory/${inventoryId}`, { partNumber: newPartNumber });
};

// Validation schema for part number
const partNumberValidationSchema = Yup.object({
  part_number: partNumberStringSchema,
});

const ComponentNumberEdit = ({
  inventoryId,
  setInventory,
  partNumber,
  supplyUnits,
  onClose,
}) => {
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [apiError, setApiError] = useState(null);

  const loadProductData = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);

    try {
      const res = await fetchProductsForInventoryId(inventoryId);
      setProducts(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error("Error loading product data:", error);
      setLoadError("Failed to load product data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [inventoryId]);

  useEffect(() => {
    loadProductData();
  }, [inventoryId, loadProductData]);

  const handleSubmit = async (values, { setSubmitting }) => {
    // Skip submission if part number hasn't changed
    if (values.part_number === partNumber) {
      onClose();
      setSubmitting(false);
      return;
    }

    try {
      setApiError(null);
      setIsSubmitting(true);

      // Call the API to update part number
      await updateComponentPartNumber(inventoryId, values.part_number);

      setInventory((prev) => ({ ...prev, part_number: values.part_number }));

      // Close modal on success
      onClose();
    } catch (error) {
      console.error("Error updating part number:", error);
      setApiError(error.message || "Failed to update part number");
    } finally {
      setIsSubmitting(false);
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={{ part_number: partNumber || "" }}
      validationSchema={partNumberValidationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleSubmit,
        handleChange,
        isSubmitting: formikSubmitting,
        isValid,
      }) => (
        <>
          <Modal.Header>Edit Inventory {normalizeId(inventoryId)}</Modal.Header>
          <Modal.Content>
            <p style={{ fontStyle: "italic", marginBottom: "20px" }}>
              Note: edits to an inventory part number affects all associated
              UPCs.
            </p>

            <Form onSubmit={handleSubmit}>
              <Form.Field>
                <label>Part Number</label>
                <Input
                  name="part_number"
                  value={values.part_number}
                  onChange={handleChange}
                  error={
                    touched.part_number && errors.part_number ? true : false
                  }
                  disabled={isSubmitting}
                />
                {touched.part_number && errors.part_number && (
                  <div style={{ color: "red", marginTop: "5px" }}>
                    {errors.part_number}
                  </div>
                )}
              </Form.Field>

              {apiError && (
                <Message negative size="tiny" style={{ marginTop: "10px" }}>
                  <Icon name="exclamation triangle" />
                  {apiError}
                </Message>
              )}
            </Form>

            {/* Supply Units Section */}
            <Header as="h5" style={{ marginTop: "30px", marginBottom: "10px" }}>
              This inventory ID has {supplyUnits?.length || 0} associated UPCs:
            </Header>
            <Table compact striped>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>UPC</Table.HeaderCell>
                  <Table.HeaderCell>Title</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {supplyUnits &&
                  supplyUnits.map((unit, index) => (
                    <Table.Row key={index}>
                      <Table.Cell>{unit.upc}</Table.Cell>
                      <Table.Cell>{unit.supply_title}</Table.Cell>
                    </Table.Row>
                  ))}
                {(!supplyUnits || supplyUnits.length === 0) && (
                  <Table.Row>
                    <Table.Cell colSpan="3" textAlign="center">
                      No UPCs associated with this inventory ID
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table>

            {/* Products Section */}

            {isLoading ? (
              <Segment basic>
                <Dimmer active inverted>
                  <Loader inverted>Loading Products...</Loader>
                </Dimmer>
              </Segment>
            ) : loadError ? (
              <Message negative>
                <Icon name="exclamation triangle" />
                {loadError}
              </Message>
            ) : (
              <>
                <Header
                  as="h5"
                  style={{ marginTop: "30px", marginBottom: "10px" }}
                >
                  This inventory ID is a component in {products.length}{" "}
                  products:
                </Header>

                <Table compact striped>
                  <Table.Header>
                    <Table.Row>
                      <Table.HeaderCell>Product ID</Table.HeaderCell>
                      <Table.HeaderCell>Title</Table.HeaderCell>
                      <Table.HeaderCell>Assembly Part Number</Table.HeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {products.length > 0 ? (
                      products.map((product) => (
                        <Table.Row key={product.product_id}>
                          <Table.Cell>{product.product_id}</Table.Cell>
                          <Table.Cell>{product.product_title}</Table.Cell>
                          <Table.Cell>
                            {product.assembly_part_number}
                          </Table.Cell>
                        </Table.Row>
                      ))
                    ) : (
                      <Table.Row>
                        <Table.Cell colSpan="3" textAlign="center">
                          No products use this component
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table>

                <Message
                  warning
                  size="tiny"
                  style={{ marginTop: "10px" }}
                  hidden={products.length < 1}
                >
                  <Icon name="warning sign" />
                  Part number changes may impact analyst calculations such as
                  ATS, MAI, MONO, etc. for above products.
                </Message>
              </>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button onClick={onClose}>Cancel</Button>
            <Button
              primary
              type="submit"
              onClick={handleSubmit}
              disabled={
                !isValid || values.part_number === partNumber || isSubmitting
              }
              loading={isSubmitting}
            >
              Save Changes
            </Button>
          </Modal.Actions>
        </>
      )}
    </Formik>
  );
};

export default ComponentNumberEdit;
